import { useState, useEffect, useCallback, useMemo } from 'react'
import { History, TrendingUp, TrendingDown, Calendar, DollarSign, Users, BarChart3 } from 'lucide-react'
import { SkeletonTable } from '@/components/ui/Skeleton'
import { Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Bar, BarChart } from 'recharts'
import { useAuth } from '@/contexts/AuthContext'

interface PurchaseBreakdown {
  new_band_cents: number
  reload_cents: number
  eticket_cents: number
  fees_cents: number
}

interface YearData {
  event_id: number
  year: number
  event_name: string
  dates: string
  event_days: number
  revenue: number
  yoy: number | null
  guests: number
  avgPerGuest: number
  total_orders: number
  total_scans: number
  ride_count: number
  game_count: number
  total_attractions: number
  best_day: { day: string; pct: number } | null
  purchase_breakdown: PurchaseBreakdown
  weatherDays: string
}

interface HistoryInsights {
  cagr: number | null
  peak_year: { year: number; revenue: number } | null
  best_day_of_week: string | null
  guest_trend: number | null
}

interface HistoryData {
  family_name: string
  years_available: number[]
  insights: HistoryInsights
  history: YearData[]
}

function fmt(cents: number): string {
  const d = cents / 100
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`
  if (d >= 1_000) return `$${(d / 1_000).toFixed(1)}K`
  return `$${d.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

function fmtDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

const PURCHASE_COLORS = { new_band: '#0070ab', reload: '#00c7ca', eticket: '#ed00be', fees: '#f59e0b' }

export function EventHistoryPage() {
  const { currentEventId, events } = useAuth()
  const activeEvent = events.find((e) => e.EventID === currentEventId)
  const familyName = activeEvent?.EventName?.replace(/\s+\d{4}\s*$/, '').trim() || 'Event'

  const [data, setData] = useState<HistoryData | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)

  const fetchHistory = useCallback(async () => {
    if (!currentEventId) return
    setLoading(true)
    try {
      const res = await fetch(`/api/dashboard/${currentEventId}/history`, { credentials: 'same-origin' })
      if (res.ok) {
        const json = await res.json()
        setData(json)
      }
    } catch { /* endpoint may not exist yet */ }
    setLoading(false)
  }, [currentEventId])

  useEffect(() => { fetchHistory() }, [fetchHistory])

  const history = data?.history ?? []
  const insights = data?.insights

  const chartData = useMemo(() =>
    history.slice().reverse().map((y) => ({
      year: y.year.toString(),
      revenue: y.revenue / 100,
      guests: y.guests,
      avgPerGuest: y.avgPerGuest,
    })),
  [history])

  const purchaseChartData = useMemo(() =>
    history.slice().reverse().map((y) => ({
      year: y.year.toString(),
      'New Band': (y.purchase_breakdown?.new_band_cents || 0) / 100,
      'Reload': (y.purchase_breakdown?.reload_cents || 0) / 100,
      'Online': (y.purchase_breakdown?.eticket_cents || 0) / 100,
      'Fees': (y.purchase_breakdown?.fees_cents || 0) / 100,
    })),
  [history])

  const hasPurchaseData = purchaseChartData.some(d => d['New Band'] > 0 || d['Reload'] > 0 || d['Online'] > 0)

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
            <History className="h-5 w-5 text-butler-600" />
          </div>
          <div>
            <h1 className="font-display font-bold text-xl text-ink-dark">{data?.family_name || familyName}</h1>
            <p className="text-sm text-ink-soft">
              {history.length > 0 ? `${history.length} years of data (${history[history.length - 1]?.year}–${history[0]?.year})` : 'Multi-year trends'}
            </p>
          </div>
        </div>
        {history.length > 0 && (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setSelectedYear(null)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                selectedYear === null ? 'bg-butler-600 text-white' : 'bg-slate-100 text-ink-soft hover:bg-butler-50'
              }`}
            >All Years</button>
            {history.map((y) => (
              <button
                key={y.year}
                type="button"
                onClick={() => setSelectedYear(y.year)}
                className={`rounded-full px-3 py-1 text-xs font-mono font-medium transition-colors ${
                  selectedYear === y.year ? 'bg-butler-600 text-white' : 'bg-slate-100 text-ink-soft hover:bg-butler-50'
                }`}
              >{y.year}</button>
            ))}
          </div>
        )}
      </div>

      {loading && <SkeletonTable rows={6} />}

      {!loading && history.length === 0 && (
        <div className="text-center py-16">
          <History className="h-12 w-12 text-butler-200 mx-auto mb-4" />
          <h2 className="font-display font-semibold text-lg text-ink-dark mb-2">No Event History</h2>
          <p className="text-sm text-ink-soft max-w-md mx-auto">
            Select an event from the sidebar to see its multi-year trends.
          </p>
        </div>
      )}

      {history.length > 0 && <>
        {/* Insight cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <InsightCard
            icon={TrendingUp}
            label="CAGR"
            value={insights?.cagr != null ? `${insights.cagr > 0 ? '+' : ''}${insights.cagr}%` : '—'}
            sub={`${history.length}yr`}
          />
          <InsightCard
            icon={DollarSign}
            label="Peak Year"
            value={insights?.peak_year ? `${insights.peak_year.year}` : '—'}
            sub={insights?.peak_year ? fmt(insights.peak_year.revenue) : undefined}
          />
          <InsightCard
            icon={Users}
            label="Guest Trend"
            value={insights?.guest_trend != null ? `${insights.guest_trend > 0 ? '+' : ''}${insights.guest_trend}%` : '—'}
            sub="first→latest"
            trend={insights?.guest_trend != null ? (insights.guest_trend >= 0 ? 'up' : 'down') : undefined}
          />
          <InsightCard
            icon={Calendar}
            label="Best Day"
            value={insights?.best_day_of_week || '—'}
            sub="most common"
          />
          <InsightCard
            icon={BarChart3}
            label="Latest Rev"
            value={fmt(history[0]?.revenue || 0)}
            sub={history[0]?.yoy != null ? `${history[0].yoy > 0 ? '+' : ''}${history[0].yoy}% YoY` : undefined}
            trend={history[0]?.yoy != null ? (history[0].yoy >= 0 ? 'up' : 'down') : undefined}
          />
        </div>

        {/* Revenue & Guest trend chart */}
        <section className="mm-card-v3 p-5">
          <h3 className="font-display font-semibold text-sm text-ink-dark mb-4">Revenue & Guest Trends</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#8697A8' }} />
                <YAxis yAxisId="revenue" tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
                <YAxis yAxisId="guests" orientation="right" tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  formatter={(value: number, name: string) =>
                    name === 'revenue' ? [`$${value.toLocaleString()}`, 'Revenue'] : [value.toLocaleString(), 'Guests']
                  }
                />
                <Area yAxisId="revenue" dataKey="revenue" fill="#0070ab" fillOpacity={0.1} stroke="#0070ab" strokeWidth={2} />
                <Line yAxisId="guests" dataKey="guests" stroke="#00c7ca" strokeWidth={2} dot={{ fill: '#00c7ca', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Revenue by Purchase Type (stacked bar) */}
        {hasPurchaseData && (
          <section className="mm-card-v3 p-5">
            <h3 className="font-display font-semibold text-sm text-ink-dark mb-4">Revenue by Purchase Type — Year over Year</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={purchaseChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#8697A8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(value: number) => `$${value.toLocaleString()}`}
                  />
                  <Bar dataKey="New Band" stackId="rev" fill={PURCHASE_COLORS.new_band} />
                  <Bar dataKey="Reload" stackId="rev" fill={PURCHASE_COLORS.reload} />
                  <Bar dataKey="Online" stackId="rev" fill={PURCHASE_COLORS.eticket} />
                  <Bar dataKey="Fees" stackId="rev" fill={PURCHASE_COLORS.fees} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-ink-soft justify-center">
              {Object.entries(PURCHASE_COLORS).map(([key, color]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
                  <span>{key === 'new_band' ? 'New Band' : key === 'eticket' ? 'Online' : key.charAt(0).toUpperCase() + key.slice(1)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Year by year table */}
        <section className="mm-card-v3 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="font-display font-semibold text-sm text-ink-dark">Year-by-Year Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Year</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Dates</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Revenue</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">YoY</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Guests</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg/Guest</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Orders</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Rides</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Best Day</th>
                </tr>
              </thead>
              <tbody>
                {history.map((y) => (
                  <tr key={y.year} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${
                    selectedYear === y.year ? 'bg-butler-50' : ''
                  }`}>
                    <td className="py-3 px-4 mm-data text-sm font-bold text-ink-dark">{y.year}</td>
                    <td className="py-3 px-4 text-xs text-ink-soft">{y.dates}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm font-medium text-ink-dark">{fmt(y.revenue)}</td>
                    <td className="py-3 px-4 text-right">
                      {y.yoy !== null ? (
                        <span className={`mm-data text-xs font-semibold inline-flex items-center gap-0.5 ${y.yoy >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {y.yoy >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {y.yoy > 0 ? '+' : ''}{y.yoy}%
                        </span>
                      ) : <span className="text-xs text-ink-hint">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink">{fmtNum(y.guests)}</td>
                    <td className="py-3 px-4 text-right mm-data text-xs text-ink-soft">{fmtDollars(y.revenue > 0 && y.guests > 0 ? Math.round(y.revenue / y.guests) : 0)}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink">{fmtNum(y.total_orders)}</td>
                    <td className="py-3 px-4 text-right mm-data text-xs text-ink-soft">{y.ride_count}</td>
                    <td className="py-3 px-4 text-xs text-ink-soft">
                      {y.best_day ? `${y.best_day.day} (${y.best_day.pct}%)` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Per-year detail if a year is selected */}
        {selectedYear && (() => {
          const y = history.find(h => h.year === selectedYear)
          if (!y) return null
          const total = y.revenue || 1
          const pb = y.purchase_breakdown || {}
          const items = [
            { label: 'New Band', cents: pb.new_band_cents || 0, color: PURCHASE_COLORS.new_band },
            { label: 'Reload', cents: pb.reload_cents || 0, color: PURCHASE_COLORS.reload },
            { label: 'Online', cents: pb.eticket_cents || 0, color: PURCHASE_COLORS.eticket },
            { label: 'Fees', cents: pb.fees_cents || 0, color: PURCHASE_COLORS.fees },
          ].filter(i => i.cents > 0)

          return (
            <section className="mm-card-v3 p-5">
              <h3 className="font-display font-semibold text-sm text-ink-dark mb-4">
                {y.event_name} — Revenue Breakdown
              </h3>
              {items.length > 0 ? (
                <>
                  <div className="flex h-3 rounded-full overflow-hidden mb-3">
                    {items.map(i => (
                      <div key={i.label} style={{ width: `${(i.cents / total) * 100}%`, backgroundColor: i.color }} />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {items.map(i => (
                      <div key={i.label}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: i.color }} />
                          <span className="text-xs text-ink-soft">{i.label}</span>
                        </div>
                        <div className="font-display font-bold text-lg text-ink-dark">{fmt(i.cents)}</div>
                        <div className="text-xs text-ink-hint">{((i.cents / total) * 100).toFixed(1)}%</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-ink-soft">No purchase type data for this year.</p>
              )}
            </section>
          )
        })()}
      </>}
    </div>
  )
}

function InsightCard({ icon: Icon, label, value, sub, trend }: { icon: typeof TrendingUp; label: string; value: string; sub?: string; trend?: 'up' | 'down' }) {
  return (
    <div className="mm-card-v3 p-3 text-center">
      <Icon className={`h-4 w-4 mx-auto mb-1 ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-500' : 'text-butler-600'}`} />
      <div className="text-[10px] uppercase tracking-wide text-ink-hint">{label}</div>
      <div className="mm-data text-lg font-bold text-ink-dark">{value}</div>
      {sub && <div className="text-[11px] text-ink-soft">{sub}</div>}
    </div>
  )
}
